import { useContent } from '@altify/utils';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import {
  useGetPersonalDataQuery,
  useSaveAddressTaxDataMutation,
  useSaveCountryOfTaxResidenceTaxDataMutation,
} from 'store/slices/personalData-api';
import {
  Button,
  Heading,
  TextInputAnimatedForm,
  Typography,
} from '@altify/components';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { taxInformationContent } from '../taxInformation.content';
import { z } from 'zod';
import CountryOfTaxResidenceModal from '../components/CountryOfTaxResidenceModal/CountryOfTaxResidenceModal';
import { CustomLoader } from 'components/CustomLoader/CustomLoader';

export const Route = createFileRoute(
  '/_authenticated/tax-information/add-home-address',
)({
  component: AddressTaxInformation,
});

const MAX_ADDRESS_LENGTH = 255;
const MAX_POSTCODE_LENGTH = 50;

function AddressTaxInformation() {
  const router = useRouter();
  const localizedContent = useContent(taxInformationContent);
  const [showCountryOfTaxResidenceModal, setShowCountryOfTaxResidenceModal] =
    useState(false);
  const {
    data: personalData,
    isLoading: personalDataLoading,
    isFetching: personalDataFetching,
  } = useGetPersonalDataQuery();
  const [saveAddress, saveAddressResult] = useSaveAddressTaxDataMutation();
  const [saveCountryOfTaxResidence, saveCountryOfTaxResidenceResult] =
    useSaveCountryOfTaxResidenceTaxDataMutation();

  const schema = z.object({
    addressLine1: z
      .string()
      .min(1, localizedContent.addressLine1Required)
      .max(MAX_ADDRESS_LENGTH, localizedContent.addressLine1MaxLength),
    city: z
      .string()
      .min(1, localizedContent.cityRequired)
      .max(MAX_ADDRESS_LENGTH, localizedContent.cityMaxLength),
    postCode: z
      .string()
      .min(1, localizedContent.postCodeRequired)
      .max(MAX_POSTCODE_LENGTH, localizedContent.postCodeMaxLength),
    addressLine2: z.string().min(0).max(MAX_ADDRESS_LENGTH).optional(),
  });

  type FormSchema = z.infer<typeof schema>;

  const {
    handleSubmit,
    formState: { isValid },
    control,
  } = useForm<FormSchema>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    values: {
      addressLine1: personalData?.addressLine1 ?? '',
      addressLine2: personalData?.addressLine2 ?? '',
      city: personalData?.residencyCity ?? '',
      postCode: personalData?.zipCode ?? '',
    },
  });

  const onSubmit = handleSubmit(async data => {
    const result = await saveAddress(data);
    if (!result.error) {
      setShowCountryOfTaxResidenceModal(true);
    }
  });

  const showLoader = personalDataLoading || personalDataFetching;

  if (showLoader) {
    return <CustomLoader page />;
  }

  return (
    <div className="w-full items-center">
      <div className="flex flex-col w-full items-center flex-1">
        <div className="flex flex-col items-center mb-10">
          <Heading level={4} className="mb-3 mt-5 text-center">
            {localizedContent.addHomeAddressTitle}
          </Heading>
        </div>
        <>
          <div className="flex flex-col items-center gap-6 w-full">
            <TextInputAnimatedForm
              name={'addressLine1'}
              label={localizedContent.addressLine1}
              placeholder={localizedContent.addressLine1}
              control={control}
              fullWidth={true}
            />
            <TextInputAnimatedForm
              name={'addressLine2'}
              label={localizedContent.addressLine2}
              placeholder={localizedContent.addressLine2}
              control={control}
              fullWidth={true}
            />
            <TextInputAnimatedForm
              name={'city'}
              label={localizedContent.city}
              placeholder={localizedContent.city}
              control={control}
              fullWidth={true}
            />
            <TextInputAnimatedForm
              name={'postCode'}
              label={localizedContent.postCode}
              placeholder={localizedContent.postCode}
              control={control}
              fullWidth={true}
            />
          </div>
          <CountryOfTaxResidenceModal
            isOpen={showCountryOfTaxResidenceModal}
            loading={saveCountryOfTaxResidenceResult.isLoading}
            onAccept={async () => {
              await saveCountryOfTaxResidence(
                personalData?.residencyCountryCode!,
              );
              setShowCountryOfTaxResidenceModal(false);
              router.navigate({
                to: '/tax-information/add-tax-identification-number',
              });
            }}
            onReject={() => {
              setShowCountryOfTaxResidenceModal(false);
              router.navigate({
                to: '/tax-information/add-country-of-tax-residence',
              });
            }}
          />
        </>
      </div>
      <div className="w-full pb-2 pt-10 items-center flex flex-col">
        {saveAddressResult.error && (
          <Typography className="my-5 text-center text-error">
            {localizedContent.unexpectedErrorOccurred}
          </Typography>
        )}
        <Button
          size="large"
          color="primary"
          disabled={!isValid}
          onPress={onSubmit}
          loading={saveAddressResult.isLoading}
          text={localizedContent.continue}
          className="w-full"
        />
      </div>
    </div>
  );
}

export default AddressTaxInformation;
